import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Lock, LayoutDashboard, Package, TrendingUp, Receipt, Sparkles,
  Plus, Trash2, DollarSign, ShoppingCart, Minus,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
} from "recharts";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const money = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CAT_COLORS = { Equipment: "#AEB358", Materials: "#4ADE80", Packaging: "#FBBF24", Other: "#60A5FA" };
const CATEGORIES = ["Equipment", "Materials", "Packaging", "Other"];
const SERIES_LABELS = { Swimbaits: "PHANTOM", Lizards: "COVERT", Worms: "PULSE" };
const typeLabel = (c) => SERIES_LABELS[c] || (c || "").toUpperCase();

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "sales", label: "Sales", icon: TrendingUp },
  { id: "expenses", label: "Expenses", icon: Receipt },
  { id: "advisor", label: "Profit Advisor", icon: Sparkles },
];

const Metric = ({ label, value, sub, accent }) => (
  <div className="bg-[#0A1118] border border-white/5 p-5" data-testid={`metric-${label}`}>
    <p className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</p>
    <p className={`font-display text-3xl mt-2 ${accent || "text-slate-100"}`}>{value}</p>
    {sub && <p className="font-mono-plex text-[10px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default function Ledger() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [costEdits, setCostEdits] = useState({});
  const [typeCostEdits, setTypeCostEdits] = useState({});
  const [applyingType, setApplyingType] = useState(null);
  const [expForm, setExpForm] = useState({ category: "Materials", amount: "", note: "", date: "" });
  const [saleForm, setSaleForm] = useState({ product_id: "", quantity: "1", price: "", customer_name: "" });
  const [saleBusy, setSaleBusy] = useState(false);
  const [stockBusy, setStockBusy] = useState(null);
  const [advice, setAdvice] = useState("");
  const [advising, setAdvising] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  const loadAll = useCallback(() => {
    api.get("/ledger/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/ledger/sales").then((r) => setSales(r.data)).catch(() => {});
    api.get("/ledger/expenses").then((r) => setExpenses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  if (loading || !user) return <div className="bg-[#04070A] min-h-screen" />;

  const saveCost = async (id) => {
    const val = parseFloat(costEdits[id]);
    if (isNaN(val)) return;
    try {
      await api.patch(`/products/${id}/cost`, { cost: val });
      toast.success("Cost updated");
      loadAll();
    } catch { toast.error("Update failed"); }
  };

  const saveCostByType = async (category) => {
    const val = parseFloat(typeCostEdits[category]);
    if (isNaN(val)) return;
    const targets = products.filter((p) => (p.category || "") === category);
    if (!targets.length) return;
    setApplyingType(category);
    try {
      await Promise.all(targets.map((p) => api.patch(`/products/${p.id}/cost`, { cost: val })));
      toast.success(`${typeLabel(category)} cost set to ${money(val)} on ${targets.length} product${targets.length === 1 ? "" : "s"}`);
      setTypeCostEdits({ ...typeCostEdits, [category]: "" });
      loadAll();
    } catch { toast.error("Bulk update failed"); }
    finally { setApplyingType(null); }
  };

  const adjustStock = async (productId, delta) => {
    setStockBusy(productId + delta);
    try {
      const { data } = await api.patch(`/products/${productId}/stock`, { delta });
      toast.success(`Stock now ${data.stock}`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Stock update failed"); }
    finally { setStockBusy(null); }
  };

  const addManualSale = async () => {
    if (!saleForm.product_id) { toast.error("Pick a bait"); return; }
    setSaleBusy(true);
    try {
      await api.post("/ledger/sales/manual", {
        product_id: saleForm.product_id,
        quantity: parseInt(saleForm.quantity) || 1,
        price: saleForm.price === "" ? null : parseFloat(saleForm.price),
        customer_name: saleForm.customer_name || null,
      });
      toast.success("Sale recorded — stock updated");
      setSaleForm({ product_id: "", quantity: "1", price: "", customer_name: "" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Could not record sale"); }
    finally { setSaleBusy(false); }
  };

  const removeManualSale = async (orderId) => {
    try {
      await api.delete(`/ledger/sales/manual/${orderId}`);
      toast.success("Sale removed — stock restored");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Could not remove sale"); }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger/expenses", { ...expForm, amount: parseFloat(expForm.amount) || 0 });
      setExpForm({ category: "Materials", amount: "", note: "", date: "" });
      toast.success("Expense added");
      loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const delExpense = async (id) => {
    try { await api.delete(`/ledger/expenses/${id}`); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const getAdvice = async () => {
    setAdvising(true);
    try {
      const { data } = await api.post("/ledger/advisor");
      setAdvice(data.advice);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Advisor unavailable"); }
    finally { setAdvising(false); }
  };

  const s = summary || {};

  return (
    <div className="bg-[#04070A] min-h-screen flex text-slate-200">
      <div className="noise-overlay" />
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/5 min-h-screen flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-white/5">
          <p className="font-display text-lg uppercase tracking-tight">BaitLedger</p>
          <p className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-orange-500 mt-1">Profit Tracker · Private</p>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 px-6 py-3 font-mono-plex text-xs tracking-widest uppercase transition-colors ${tab === n.id ? "bg-orange-500/10 text-orange-400 border-l-2 border-orange-500" : "text-slate-400 hover:text-slate-200 border-l-2 border-transparent"}`}
              data-testid={`ledger-tab-${n.id}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="px-6 py-5 border-t border-white/5 flex flex-col gap-3">
          <Link to="/admin" className="flex items-center gap-2 font-mono-plex text-[10px] tracking-widest uppercase text-slate-400 hover:text-orange-500 transition-colors" data-testid="ledger-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Store Admin
          </Link>
          <span className="flex items-center gap-2 font-mono-plex text-[10px] tracking-widest uppercase text-slate-600">
            <Lock className="w-3 h-3" /> Owner only
          </span>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 p-8 md:p-10">
        {tab === "dashboard" && (
          <div data-testid="ledger-dashboard">
            <h1 className="font-display text-4xl uppercase tracking-tight mb-8">Dashboard</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Metric label="Total Revenue" value={money(s.revenue)} sub={`${s.orders || 0} orders · ${s.units || 0} units`} />
              <Metric label="Net Profit" value={money(s.net_profit)} sub={`After ${money(s.overhead)} overhead`} accent={(s.net_profit || 0) >= 0 ? "text-green-400" : "text-red-400"} />
              <Metric label="Gross Margin" value={`${(s.gross_margin || 0).toFixed(1)}%`} sub={`Gross profit ${money(s.gross_profit)}`} accent="text-green-400" />
              <Metric label="Net Margin" value={`${(s.net_margin || 0).toFixed(1)}%`} sub={`COGS ${money(s.cogs)}`} accent="text-green-400" />
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-[#0A1118] border border-white/5 p-6">
                <p className="font-display text-lg uppercase tracking-tight mb-6">Revenue vs Cost</p>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={s.monthly && s.monthly.length ? s.monthly : [{ month: "—", revenue: 0, cost: 0 }]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e2a36" />
                    <XAxis dataKey="month" stroke="#475569" fontSize={11} />
                    <YAxis stroke="#475569" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#04070A", border: "1px solid #1e2a36" }} />
                    <Line type="monotone" dataKey="revenue" stroke="#AEB358" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                    <Line type="monotone" dataKey="cost" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} name="Cost" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-[#0A1118] border border-white/5 p-6">
                <p className="font-display text-lg uppercase tracking-tight mb-4">Expense Breakdown</p>
                {(s.expense_breakdown || []).length ? (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={s.expense_breakdown} dataKey="amount" nameKey="category" innerRadius={45} outerRadius={75} paddingAngle={3}>
                          {s.expense_breakdown.map((e) => <Cell key={e.category} fill={CAT_COLORS[e.category] || "#60A5FA"} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 flex flex-col gap-2">
                      {s.expense_breakdown.map((e) => (
                        <div key={e.category} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: CAT_COLORS[e.category] }} />{e.category}</span>
                          <span className="font-mono-plex">{money(e.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <p className="text-slate-500 text-sm">No expenses logged yet.</p>}
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mt-4">
              <div className="bg-[#0A1118] border border-white/5 p-6">
                <p className="font-display text-lg uppercase tracking-tight mb-4">Profit by Product</p>
                {(s.profit_by_product || []).length ? s.profit_by_product.map((p) => (
                  <div key={p.name} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                    <span>{p.name} <span className="text-slate-500">· {p.units} sold</span></span>
                    <span className={`font-mono-plex ${p.profit >= 0 ? "text-green-400" : "text-red-400"}`}>{money(p.profit)}</span>
                  </div>
                )) : <p className="text-slate-500 text-sm">No product sales yet.</p>}
              </div>
              <div className="bg-[#0A1118] border border-white/5 p-6">
                <p className="font-display text-lg uppercase tracking-tight mb-4">Inventory</p>
                {[["Products", s.products_count || 0], ["Units Sold", s.units || 0], ["Overhead", money(s.overhead)], ["COGS", money(s.cogs)]].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2.5 border-b border-white/5 text-sm">
                    <span className="text-slate-400">{k}</span><span className="font-mono-plex">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="ledger-products">
            <h1 className="font-display text-4xl uppercase tracking-tight mb-2">Products</h1>
            <p className="text-slate-400 text-sm mb-6">Set your per-unit cost (COGS) so profit calculates automatically. Pulled from your live store.</p>

            {/* Quick set by type — each bait type has its own pack cost */}
            {(() => {
              const types = [...new Set(products.map((p) => p.category).filter((c) => c && c.trim()))].sort();
              if (!types.length) return null;
              return (
                <div className="bg-[#0A1118] border border-white/5 p-5 mb-6" data-testid="cost-by-type">
                  <p className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-4">Quick set by type — applies to every bait of that type</p>
                  <div className="flex flex-wrap gap-4">
                    {types.map((t) => {
                      const count = products.filter((p) => p.category === t).length;
                      return (
                        <div key={t} className="flex items-center gap-2 border border-white/10 px-3 py-2" data-testid={`type-cost-${typeLabel(t).toLowerCase()}`}>
                          <span className="font-mono-plex text-xs text-orange-400 tracking-widest">{typeLabel(t)}</span>
                          <span className="font-mono-plex text-[10px] text-slate-600">{count} item{count === 1 ? "" : "s"}</span>
                          <span className="text-slate-500 text-sm">$</span>
                          <input
                            type="number" step="any" placeholder="0.00"
                            value={typeCostEdits[t] || ""}
                            onChange={(e) => setTypeCostEdits({ ...typeCostEdits, [t]: e.target.value })}
                            className="w-20 bg-transparent border-b border-white/20 py-1 text-right focus:border-orange-500 focus:outline-none text-sm"
                            data-testid={`type-cost-input-${typeLabel(t).toLowerCase()}`}
                          />
                          <button
                            onClick={() => saveCostByType(t)}
                            disabled={applyingType === t}
                            className="font-mono-plex text-[10px] tracking-widest uppercase text-orange-400 hover:text-orange-300 disabled:opacity-50"
                            data-testid={`type-cost-apply-${typeLabel(t).toLowerCase()}`}
                          >
                            {applyingType === t ? "…" : "Apply"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="border border-white/5">
              <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">
                <span className="col-span-4">Product</span><span className="col-span-2 text-right">Price</span><span className="col-span-2 text-center">Stock</span><span className="col-span-2 text-right">Unit Cost</span><span className="col-span-2 text-right">Margin</span>
              </div>
              {products.map((p) => {
                const cost = costEdits[p.id] !== undefined ? costEdits[p.id] : (p.cost || 0);
                const margin = p.price ? ((p.price - (parseFloat(cost) || 0)) / p.price) * 100 : 0;
                return (
                  <div key={p.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ledger-product-${p.id}`}>
                    <span className="col-span-4 font-display uppercase text-sm truncate flex items-center gap-2 min-w-0">
                      <span className="truncate">{p.name}</span>
                      <span className="shrink-0 font-mono-plex text-[9px] tracking-[0.15em] text-orange-400 bg-orange-950/60 border border-orange-500/30 px-1.5 py-0.5" data-testid={`type-badge-${p.id}`}>
                        {typeLabel(p.category)}
                      </span>
                    </span>
                    <span className="col-span-2 text-right font-mono-plex text-sm">{money(p.price)}</span>
                    <div className="col-span-2 flex items-center justify-center gap-1.5" data-testid={`stock-control-${p.id}`}>
                      <button
                        onClick={() => adjustStock(p.id, -1)}
                        disabled={stockBusy || !p.in_stock}
                        className="w-6 h-6 flex items-center justify-center border border-white/15 text-slate-400 hover:border-orange-500 hover:text-orange-400 disabled:opacity-30 transition-colors"
                        data-testid={`stock-minus-${p.id}`}
                      ><Minus className="w-3 h-3" /></button>
                      <span className={`font-mono-plex text-sm w-8 text-center ${p.in_stock ? "text-slate-200" : "text-red-400"}`} data-testid={`stock-count-${p.id}`}>{p.stock ?? 0}</span>
                      <button
                        onClick={() => adjustStock(p.id, 1)}
                        disabled={stockBusy}
                        className="w-6 h-6 flex items-center justify-center border border-white/15 text-slate-400 hover:border-orange-500 hover:text-orange-400 disabled:opacity-30 transition-colors"
                        data-testid={`stock-plus-${p.id}`}
                      ><Plus className="w-3 h-3" /></button>
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <span className="text-slate-500">$</span>
                      <input
                        type="number" step="any"
                        value={cost}
                        onChange={(e) => setCostEdits({ ...costEdits, [p.id]: e.target.value })}
                        className="w-20 bg-transparent border-b border-white/20 py-1 text-right focus:border-orange-500 focus:outline-none text-sm"
                        data-testid={`cost-input-${p.id}`}
                      />
                      <button onClick={() => saveCost(p.id)} className="font-mono-plex text-[10px] tracking-widest uppercase text-orange-400 hover:text-orange-300" data-testid={`cost-save-${p.id}`}>Save</button>
                    </div>
                    <span className={`col-span-2 text-right font-mono-plex text-sm ${margin >= 0 ? "text-green-400" : "text-red-400"}`}>{margin.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "sales" && (
          <div data-testid="ledger-sales">
            <h1 className="font-display text-4xl uppercase tracking-tight mb-2">Sales</h1>
            <p className="text-slate-400 text-sm mb-6">Every paid or shipped order — revenue is pulled automatically. Record in-person sales below.</p>

            {/* Manual sale entry — craft shows, in-person, etc. */}
            <div className="bg-[#0A1118] border border-white/5 p-5 mb-8" data-testid="manual-sale-form">
              <p className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-4">Record an offline sale — updates stock automatically</p>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block font-mono-plex text-[10px] text-slate-500 uppercase tracking-widest mb-1">Bait</label>
                  <select
                    value={saleForm.product_id}
                    onChange={(e) => setSaleForm({ ...saleForm, product_id: e.target.value })}
                    className="bg-[#04070A] border border-white/15 px-3 py-2 text-sm text-slate-200 focus:border-orange-500 focus:outline-none min-w-52"
                    data-testid="manual-sale-product"
                  >
                    <option value="">Choose a bait…</option>
                    {products.filter((p) => !p.custom).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {typeLabel(p.category)} ({p.stock ?? 0} in stock)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-mono-plex text-[10px] text-slate-500 uppercase tracking-widest mb-1">Qty</label>
                  <input
                    type="number" min="1"
                    value={saleForm.quantity}
                    onChange={(e) => setSaleForm({ ...saleForm, quantity: e.target.value })}
                    className="w-20 bg-[#04070A] border border-white/15 px-3 py-2 text-sm text-right focus:border-orange-500 focus:outline-none"
                    data-testid="manual-sale-qty"
                  />
                </div>
                <div>
                  <label className="block font-mono-plex text-[10px] text-slate-500 uppercase tracking-widest mb-1">Price/pack <span className="text-slate-600">(blank = list price)</span></label>
                  <input
                    type="number" step="any" placeholder="auto"
                    value={saleForm.price}
                    onChange={(e) => setSaleForm({ ...saleForm, price: e.target.value })}
                    className="w-24 bg-[#04070A] border border-white/15 px-3 py-2 text-sm text-right focus:border-orange-500 focus:outline-none"
                    data-testid="manual-sale-price"
                  />
                </div>
                <div>
                  <label className="block font-mono-plex text-[10px] text-slate-500 uppercase tracking-widest mb-1">Customer <span className="text-slate-600">(optional)</span></label>
                  <input
                    type="text" placeholder="In-person sale"
                    value={saleForm.customer_name}
                    onChange={(e) => setSaleForm({ ...saleForm, customer_name: e.target.value })}
                    className="w-44 bg-[#04070A] border border-white/15 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
                    data-testid="manual-sale-customer"
                  />
                </div>
                <button
                  onClick={addManualSale}
                  disabled={saleBusy}
                  className="bg-orange-500 text-[#04070A] font-mono-plex text-[11px] tracking-widest uppercase font-bold px-5 py-2.5 hover:bg-orange-600 transition-colors disabled:opacity-50"
                  data-testid="manual-sale-submit"
                >
                  {saleBusy ? "Saving…" : "Add Sale"}
                </button>
              </div>
            </div>

            {sales.length ? (
              <div className="border border-white/5">
                {sales.map((o) => (
                  <div key={o.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-sm" data-testid={`sale-${o.id}`}>
                    <div>
                      <p className="font-display uppercase flex items-center gap-2">
                        {o.customer_name}
                        {o.payment_method === "manual" && (
                          <span className="font-mono-plex text-[9px] tracking-[0.15em] text-slate-400 border border-white/15 px-1.5 py-0.5">OFFLINE</span>
                        )}
                        {o.status === "shipped" && (
                          <span className="font-mono-plex text-[9px] tracking-[0.15em] text-green-400 border border-green-500/30 px-1.5 py-0.5">SHIPPED</span>
                        )}
                      </p>
                      <p className="font-mono-plex text-[10px] text-slate-500">{(o.created_at || "").slice(0, 10)} · {o.payment_method} · {o.items?.reduce((a, i) => a + i.quantity, 0)} units</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono-plex text-green-400">{money(o.total)}</span>
                      {o.payment_method === "manual" && (
                        <button onClick={() => removeManualSale(o.id)} className="text-slate-500 hover:text-red-400 transition-colors" title="Remove sale & restore stock" data-testid={`sale-delete-${o.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-slate-500">
                <ShoppingCart className="w-10 h-10 mb-4 opacity-40" />
                <p className="text-sm">No sales yet. Orders appear here once paid or shipped.</p>
              </div>
            )}
          </div>
        )}

        {tab === "expenses" && (
          <div data-testid="ledger-expenses">
            <h1 className="font-display text-4xl uppercase tracking-tight mb-8">Expenses</h1>
            <form onSubmit={addExpense} className="bg-[#0A1118] border border-white/5 p-6 grid sm:grid-cols-5 gap-4 items-end mb-8">
              <div className="sm:col-span-1">
                <label className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Category</label>
                <select value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="exp-category">
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0A1118]">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Amount</label>
                <input type="number" step="any" required value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="exp-amount" />
              </div>
              <div>
                <label className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Date</label>
                <input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="exp-date" />
              </div>
              <div>
                <label className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Note</label>
                <input value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 focus:border-orange-500 focus:outline-none" data-testid="exp-note" />
              </div>
              <button type="submit" className="bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-widest uppercase py-3 flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors" data-testid="exp-add">
                <Plus className="w-4 h-4" /> Add
              </button>
            </form>
            {expenses.length ? (
              <div className="border border-white/5">
                {expenses.map((e) => (
                  <div key={e.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-sm" data-testid={`expense-${e.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full" style={{ background: CAT_COLORS[e.category] || "#60A5FA" }} />
                      <span className="font-display uppercase">{e.category}</span>
                      <span className="text-slate-500">{e.note} {e.date && `· ${e.date}`}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono-plex">{money(e.amount)}</span>
                      <button onClick={() => delExpense(e.id)} className="text-slate-500 hover:text-red-400 transition-colors" data-testid={`expense-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-sm">No expenses yet — add your first above.</p>}
          </div>
        )}

        {tab === "advisor" && (
          <div data-testid="ledger-advisor">
            <h1 className="font-display text-4xl uppercase tracking-tight mb-2">Profit Advisor</h1>
            <p className="text-slate-400 text-sm mb-8">AI-powered, plain-English suggestions based on your real numbers.</p>
            <button onClick={getAdvice} disabled={advising} className="bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-widest uppercase px-6 py-3 flex items-center gap-2 hover:bg-orange-600 transition-colors disabled:opacity-50 mb-8" data-testid="advisor-generate">
              <Sparkles className="w-4 h-4" /> {advising ? "Analyzing…" : "Generate Advice"}
            </button>
            {advice && (
              <div className="bg-[#0A1118] border border-white/5 p-8 max-w-3xl whitespace-pre-wrap text-slate-300 leading-relaxed text-sm" data-testid="advisor-output">
                {advice}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
